import { createContext, ReactNode } from "react";
import { Avatar, Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  getAppointmentStatus,
  APPOINTMENT_STATUS,
  AppointmentStatus
} from "../../../hooks/useAppointments";
import { Appointment } from "../../../state/appointments";
import { typography } from "../../../utils/typography";
import { semanticColors } from "../../../utils/colors";

/**
 * patientName (lowercased) → picture URL. Lets the custom calendar agenda view
 * (rendered deep inside react-big-calendar) reach the doctor's picture map.
 */
export const PictureMapContext = createContext<Record<string, string>>({});

export interface AppointmentColumn {
  key: string;
  label: string;
  /** Fixed column width; omit for the flexible column. */
  width?: string;
  render: (appt: Appointment) => ReactNode;
}

const containerSx = {
  background: "#fff",
  borderRadius: "24px",
  overflow: "hidden",
  boxShadow: "0px 0px 4px rgba(30,39,79,0.12)",
  mt: 2
};

const headerRowSx = {
  display: "flex",
  alignItems: "center",
  height: "48px",
  background: semanticColors.gradient.surface,
  px: "20px"
};

const headerCellSx = {
  ...typography.body.lBold,
  color: semanticColors.text.inverse,
  whiteSpace: "nowrap" as const
};

const rowSx = (index: number) => ({
  display: "flex",
  alignItems: "center",
  height: "64px",
  px: "20px",
  background: semanticColors.surface.canvas,
  borderTop: index > 0 ? `0.5px solid ${semanticColors.border.light}` : "none"
});

const cellSx = (col: AppointmentColumn) =>
  col.width ? { width: col.width, flexShrink: 0 } : { flex: 1, minWidth: 0 };

/**
 * Shared styled appointments table (green header, rounded card, avatar rows).
 * Used by the history tab and the calendar's agenda view so they look identical.
 * `translate="no"` keeps the browser from mistranslating the column headers.
 */
export default function AppointmentsTable({
  columns,
  rows
}: {
  columns: AppointmentColumn[];
  rows: Appointment[];
}) {
  return (
    <Box translate="no" sx={containerSx}>
      {/* Header */}
      <Box sx={headerRowSx}>
        {columns.map(col => (
          <Box key={col.key} sx={cellSx(col)}>
            <Typography sx={headerCellSx}>{col.label}</Typography>
          </Box>
        ))}
      </Box>

      {/* Rows */}
      {rows.map((appt, i) => (
        <Box key={appt.id} sx={rowSx(i)}>
          {columns.map(col => (
            <Box key={col.key} sx={cellSx(col)}>
              {col.render(appt)}
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}

// ── Shared cell renderers ───────────────────────────────────────────────────

const textCellSx = {
  ...typography.body.l,
  color: semanticColors.text.primary
};

export function TextCell({ children }: { children: ReactNode }) {
  return <Typography sx={textCellSx}>{children}</Typography>;
}

const patientCellSx = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  minWidth: 0
};

const patientAvatarSx = {
  width: 40,
  height: 40,
  flexShrink: 0,
  border: `1.5px solid ${semanticColors.border.middle}`
};

const patientNameSx = {
  ...typography.body.lBold,
  color: semanticColors.text.primary,
  textTransform: "uppercase" as const,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap" as const
};

/** Patient name with avatar (history table). */
export function PatientNameCell({
  appt,
  pictureUrl
}: {
  appt: Appointment;
  pictureUrl?: string;
}) {
  return (
    <Box sx={patientCellSx}>
      <Avatar src={pictureUrl} alt={appt.patientName} sx={patientAvatarSx} />
      <Typography sx={patientNameSx}>{appt.patientName}</Typography>
    </Box>
  );
}

const statusLabelSx = (color: string) => ({ ...typography.body.mBold, color });

const statusColor: Record<AppointmentStatus, string> = {
  [APPOINTMENT_STATUS.Scheduled]: semanticColors.text.secondary,
  [APPOINTMENT_STATUS.Done]: semanticColors.feedback.ok,
  [APPOINTMENT_STATUS.Cancelled]: semanticColors.status.error,
  [APPOINTMENT_STATUS.Lapsed]: semanticColors.status.inProgress
};

const statusLabelKey = {
  [APPOINTMENT_STATUS.Scheduled]: "videoConsultations.statusScheduled",
  [APPOINTMENT_STATUS.Done]: "videoConsultations.statusDone",
  [APPOINTMENT_STATUS.Cancelled]: "videoConsultations.statusCancelled",
  [APPOINTMENT_STATUS.Lapsed]: "videoConsultations.statusLapsed"
} as const;

/** Realizada / Cancelada / Expirada / Agendada, colored. */
export function StatusCell({ appt }: { appt: Appointment }) {
  const { t } = useTranslation();
  const status = getAppointmentStatus(appt);
  return (
    <Typography sx={statusLabelSx(statusColor[status])}>
      {t(statusLabelKey[status])}
    </Typography>
  );
}
